import { useEffect, useMemo, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  cagesApi,
  CAGE_TYPE_LABELS,
  type CageOption,
} from '../api/cages';

interface CageSelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (cageNumber: string) => void;
  required?: boolean;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  allowCreate?: boolean;
  includeInactive?: boolean;
  placeholder?: string;
  className?: string;
  error?: boolean;
}

export default function CageSelect({
  id = 'cageNumber',
  name,
  value,
  onChange,
  required = false,
  disabled = false,
  allowEmpty = false,
  emptyLabel = 'All cages',
  allowCreate = true,
  includeInactive = false,
  placeholder = 'Search or select a cage…',
  className = 'mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm pl-10 pr-10',
  error = false,
}: CageSelectProps) {
  const [options, setOptions] = useState<CageOption[]>([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    cagesApi
      .options(includeInactive)
      .then((rows) => {
        if (!cancelled) setOptions(rows);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [includeInactive]);

  const selected = useMemo(
    () => options.find((o) => o.cageNumber === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = [o.cageNumber, o.location, CAGE_TYPE_LABELS[o.cageType]]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const typed = query.trim();
  const showCreate =
    allowCreate &&
    typed.length > 0 &&
    !options.some((o) => o.cageNumber.toLowerCase() === typed.toLowerCase());

  const display = selected
    ? selected.cageNumber
    : value || '';

  return (
    <Combobox
      as="div"
      value={value || null}
      onChange={(next: string | null) => {
        onChange(next ?? '');
        setQuery('');
      }}
      disabled={disabled}
      nullable
    >
      {required ? <input type="hidden" name={name || id} value={value} required /> : null}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
        </div>
        <Combobox.Input
          id={id}
          className={`${className} ${error ? 'border-red-300' : ''}`}
          displayValue={() => display}
          onChange={(event) => setQuery(event.target.value)}
          onBlur={() => {
            if (allowCreate && typed && typed !== value) {
              onChange(typed);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
        </Combobox.Button>

        <Combobox.Options className="absolute z-30 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm max-h-60">
          {allowEmpty ? (
            <Combobox.Option
              value=""
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                  active ? 'bg-cyan-600 text-white' : 'text-slate-900'
                }`
              }
            >
              {emptyLabel}
            </Combobox.Option>
          ) : null}
          {filtered.map((option) => (
            <Combobox.Option
              key={option.id}
              value={option.cageNumber}
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                  active ? 'bg-cyan-600 text-white' : 'text-slate-900'
                }`
              }
            >
              {({ active, selected: isSelected }) => (
                <>
                  <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                    {option.cageNumber}
                    <span className={active ? 'text-cyan-100' : 'text-slate-500'}>
                      {' '}
                      · {CAGE_TYPE_LABELS[option.cageType]}
                      {option.location ? ` · ${option.location}` : ''}
                    </span>
                  </span>
                  {isSelected ? (
                    <span
                      className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                        active ? 'text-white' : 'text-cyan-600'
                      }`}
                    >
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  ) : null}
                </>
              )}
            </Combobox.Option>
          ))}
          {showCreate ? (
            <Combobox.Option
              value={typed}
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 pl-3 pr-3 ${
                  active ? 'bg-cyan-600 text-white' : 'text-cyan-800'
                }`
              }
            >
              Use “{typed}” (will be added to the catalog)
            </Combobox.Option>
          ) : null}
          {!filtered.length && !showCreate && !allowEmpty ? (
            <div className="px-3 py-2 text-sm text-slate-500">No cages match</div>
          ) : null}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}
